// Games compared: one row per game with the same stats as the Overview game tiles.
import moment from 'moment';
import { formatDuration, formatClock } from '../../live/liveMetrics';
import { C, STATUS_RGB, hexToRgb, pageHeader, table, queueStatus, countStatus } from './pdfTheme';

const COUNT_AVG_LOW = 20;
const COUNT_AVG_HIGH = 40;

export const generateGamesComparedPage = (pdf, { gameCards, isCountMode, formatTransactionTime }) => {
  pdf.addPage();
  let y = pageHeader(
    pdf,
    'Game summaries',
    'Each selected game, all sections averaged, over the time window. Colors match the charts.'
  );

  const peakAt = (p) => (p ? ` · ${formatClock(new Date(p.x))}` : '');
  // Date only (full game names are on the cover) so the column never crowds the next one
  const gameCell = (g) => ({ text: moment(g.date).format('ddd, MMM D, YYYY'), swatch: hexToRgb(g.color), bold: true });
  const noData = { text: 'No data', color: C.subtle };

  const columns = isCountMode
    ? [
        { header: 'Game', width: 62, render: gameCell },
        { header: 'Avg count', align: 'center', render: (g) => (g.hasData ? { text: `${g.avg} ppl`, bold: true, color: STATUS_RGB[countStatus(g.avg, COUNT_AVG_LOW, COUNT_AVG_HIGH)] } : noData) },
        { header: 'Peak count', align: 'center', render: (g) => (g.hasData ? `${g.peak?.y ?? 0} ppl${peakAt(g.peak)}` : '') },
        { header: 'Busy time', align: 'center', render: (g) => (g.hasData ? { text: formatDuration(g.over.minutes), color: g.over.minutes > 0 ? C.bad : C.good } : '') },
        { header: 'Busiest section', align: 'center', render: (g) => (g.busiest ? `${g.busiest.id} (${g.busiest.avg} ppl)` : '') },
      ]
    : [
        { header: 'Game', width: 44, render: gameCell },
        { header: 'Avg queue', width: 18, align: 'center', render: (g) => (g.hasData ? { text: `${g.avg} min`, bold: true, color: STATUS_RGB[queueStatus(g.avg)] } : noData) },
        {
          header: 'Peak queue',
          width: 30,
          align: 'center',
          render: (g) => (g.hasData
            ? { text: `${g.peak?.y ?? 0}${g.peak?.overflow ? '+' : ''} min${peakAt(g.peak)}`, color: g.peak?.overflow ? C.bad : C.text, size: 8 }
            : ''),
        },
        { header: 'Over target', width: 28, align: 'center', render: (g) => (g.hasData ? { text: `${formatDuration(g.over.minutes)} (${Math.round(g.over.share * 100)}%)`, color: g.over.minutes > 0 ? C.bad : C.good } : '') },
        {
          header: 'Overflow',
          width: 26,
          align: 'center',
          render: (g) => (g.hasData
            ? g.overflow.minutes > 0
              ? { text: `${formatDuration(g.overflow.minutes)} · ${g.overflow.episodes} ${g.overflow.episodes === 1 ? 'time' : 'times'}`, color: C.bad, bold: true, size: 7.5 }
              : { text: 'None', color: C.subtle }
            : ''),
        },
        { header: 'Avg txn', width: 14, align: 'center', render: (g) => (g.hasData ? formatTransactionTime(g.txnAvg) : '') },
        { header: 'Busiest', width: 20, align: 'center', render: (g) => (g.busiest ? `${g.busiest.id} (${g.busiest.avg}m)` : '') },
      ];

  y = table(pdf, y, columns, gameCards, { rowHeight: 10 });

  y += 6;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(C.subtle[0], C.subtle[1], C.subtle[2]);
  pdf.text(
    isCountMode
      ? 'Busy time: average count across sections above 40 people. Busiest section: highest average count in that game.'
      : 'Over target: average queue across sections above 10 min (overflow counts as over). Overflow: total time and number of separate times. "+" values are minimums.',
    15,
    y,
    { maxWidth: pdf.internal.pageSize.getWidth() - 30 }
  );
};
